import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { CoverMedia } from "@/components/CoverMedia";
import { ReviewsStrip } from "@/components/Testimonials";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["portfolio"],
    queryFn: () => api.get("/portfolio").then((r) => r.data),
  });

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))], [items]);
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="pt-28 pb-24 max-w-6xl mx-auto px-5" data-testid="portfolio-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Our Work</p>
      <h1 className="font-display text-5xl md:text-6xl text-white mt-3 mb-8">Portfolio</h1>

      <div className="flex flex-wrap gap-2 mb-10 no-scrollbar">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`px-5 py-2 rounded-full text-sm font-mono-label uppercase tracking-wide transition-colors ${
              filter === c ? "btn-gold" : "border border-white/15 text-white/70 hover:border-[#D4AF37]/50"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-white/40">Loading…</div>
      ) : (
        <div className="columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {filtered.map((p, i) => (
            <Link key={p.id} to={`/portfolio/${p.slug}`} data-testid={`portfolio-item-${i}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 6) * 0.05 }}
                className={`group relative mb-4 break-inside-avoid rounded-xl overflow-hidden border border-white/10 ${
                  i % 3 === 0 ? "aspect-[3/4]" : "aspect-square"
                }`}
              >
                <CoverMedia item={p} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                {p.featured && (
                  <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded">Featured</span>
                )}
                <div className="absolute bottom-0 p-4">
                  <p className="font-mono-label uppercase text-[10px] tracking-[0.2em] text-[#D4AF37]">{p.category}</p>
                  <h3 className="font-display text-base text-white leading-tight mt-0.5">{p.title}</h3>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      )}
      {!isLoading && filtered.length === 0 && <div className="text-white/40">No projects in this category yet.</div>}
      <ReviewsStrip />
    </div>
  );
}
